import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native'

import InputSelect from '../core/InputSelect';
import { Bound, useMagic } from 'opidcore';
import { useOpidNavigator } from '../core/OpidNavigator';
import { Tabular, TabularColumn } from '../core/Tabular';
import { BasicText, FlexBlock, FlexBlockWrapped, FlexElement, Heading2, Heading3, Heading4, Label, Loading, MetricStackTitle, PageWrapper, ShinyBlockOuter, ShinyBody, ShinyHeading } from '../Styles';
import { CircleChart, niceRound } from '../Things';
import Util from '../core/Util';

const GroupComparison = ({ portalData, rawData = [] }) => {
    const bound = useMagic({showing: "metric_tonnes_diverted", group: "group"});
    const [showing, setShowing] = useState(bound.magicalGet("showing"));
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [updating, setUpdating] = useState(false);

    useEffect(()=>{
        updateData();
    }, [rawData]);

    useEffect(()=>{
        if(updating){
            updateData();
            setUpdating(false);
        }
    }, [updating]);

    useEffect(()=>{
        bound.magicalState("group", ()=>setUpdating(true));
        bound.magicalState("showing", ()=>setUpdating(true));
    }, [bound]);

    const updateData = () => {
        const groupedData = _.groupBy(rawData, bound.magicalGet("group"));
        let runningTotal = 0;

        const chartData = _.map(_.keys(groupedData), (groupKey)=>{ 
            const mySum = _.sumBy(groupedData[groupKey], bound.magicalGet("showing"));
            runningTotal += mySum;
            return {label: groupKey, value: mySum} ;
        });

        setTotal(runningTotal);
        setData(chartData);
        setShowing(bound.magicalGet("showing"));
    }
    
    const groupOptions = _.size(_.uniq(_.map(rawData, 'group'))) > 1 ? 
        [{value: "group", label: "Region"}, {value: "province", label: "Province"}] :
        [{value: "province", label: "Province"}];

    if (bound.magicalGet("group") == "group" && groupOptions.length == 1){
        bound.magicalSet("group", "province");
    }

    if (rawData.length == 0){
        return null;
    }

    return <ShinyBlockOuter minHeight={400} flexBasis={Util.isMobile()? "95%" : "49%"}>
        <ShinyHeading>
            <MetricStackTitle>{"Region Comparison"} ({portalData.dateRange.length || 0} Months)</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody flexDirection="row" style={{flexWrap: "wrap"}}>
            <View style={{flexBasis: Util.isMobile()? "95%" : "49%", marginBottom: 20}}>
                <Bound to={bound}>
                    <FlexBlock style={{alignItems: 'flex-start', justifyContent: "flex-start", flexWrap: "wrap"}}>
                        <InputSelect label="Showing:" flexDirection="row" field="showing" showEmpty={false} options={[{value: "metric_tonnes_diverted", label: "Diverted Materials"}, {value: "cost_avoidance", label: "Cost Avoidance"}, {value: "ghg_reduction", label: "GHG Emissions Reduced"}]}/>
                        <InputSelect label="Group:" flexDirection="row" field="group" showEmpty={false} options={groupOptions}/>
                    </FlexBlock>
                </Bound>
                <CircleChart data={data} units="MT" centeredMetric={total} labelFontSize={14} width={500} minDisplayAngle={20}/>
            </View>
            <SiteRankingTabular flexBasis={Util.isMobile()? "95%" : "49%"} rawData={rawData} topRanking={5} includeRelated={false} showRankBy={false} defaultShowing={showing == "metric_tonnes_diverted"? "percent_diverted" : showing} rowHeight={60}/>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const SiteRankingTabular = ({ rawData, topRanking = undefined, includeRelated = true, showRankBy=true, defaultShowing = "percent_diverted", rowHeight = undefined, flexBasis="95%"}) => {
    const navigation = useOpidNavigator();
    const [data, setData] = useState([]);
    const pageSize = useRef(10);
    const boundRef = useRef({showing: defaultShowing, group: "group"});
    const [title, setTitle] = useState("");

    useEffect(()=>{
        if(rawData != undefined){
            let newData = [];
            const showing = boundRef.current.showing;

            let displayedRank = 1;
            let theIndex = 1;
            let previousValue = 0;
            newData = _.map(_.orderBy(rawData, showing, "desc"), (s) => {
                if(Math.abs(s[showing] - previousValue) > 0.0001){
                    displayedRank = theIndex;
                    previousValue = s[showing];
                }
                
                s.overallRank = displayedRank;

                theIndex++;

                return s;
            });

            if (topRanking != undefined) {
                if (newData.length >= topRanking){
                    newData = newData.slice(0, topRanking);
                }

                pageSize.current = null;
            }

            setData(newData);
        }
    },[rawData, title]);

    useEffect(()=>{
        boundRef.current.showing = defaultShowing;
        updateTitle();
    }, [defaultShowing]);

    const updateTitle = (a,b,c)=>{
        let newTitle = title + "";
        if(boundRef.current.showing == "percent_diverted"){
            newTitle = "Diversion (%)";
        }
    
        if(boundRef.current.showing == "cost_avoidance"){
            newTitle = "Cost Avoidance ($)";
        }
    
        if(boundRef.current.showing == "ghg_reduction"){
            newTitle = "GHG Reduction (MT CO₂)";
        }

        setTitle(newTitle);
    }

    const RankingTable = styled.View`
        width: ${props => props.flexBasis != undefined? props.flexBasis: "95%"};
    `;

    const SortingBlock = styled(FlexBlock)`
        justify-content: flex-end;
        align-items: center;
        flex-direction: row;
        margin-bottom: 15px;
    `;

    const pickerStyle = {
        width: 250
    };

    const itemStyle = {
        
    };

    return <RankingTable flexBasis={flexBasis}>
        <Bound to={boundRef.current}>
            { showRankBy ? <SortingBlock>
                <InputSelect 
                    field="showing" 
                    label="Rank By:"
                    flexDirection="row"
                    showEmpty={false} 
                    itemStyle={itemStyle} 
                    pickerStyle={pickerStyle}
                    onChange={(a,b,c)=>updateTitle(a,b,c)}
                    options={[
                        {value: "percent_diverted", label: "Diversion %"}, 
                        {value: "cost_avoidance", label: "Cost Avoidance"}, 
                        {value: "ghg_reduction", label: "GHG Emissions Reduced"}
                    ]}
                />
            </SortingBlock> : null }
        </Bound>

        <Tabular data={data} pageSize={pageSize.current} rowHeight={rowHeight} scrollable={false}>
            <TabularColumn key="rank" data={(r) => <BasicText>{r.overallRank}</BasicText>} title="Rank" width={Util.isMobile()? null : 50}/>
            <TabularColumn key="mt_name" data={(r) => <BasicText onPress={()=>navigation.goto("/site/" + r.site_id)}>{r.name}</BasicText>} title="Name" />
            {!Util.isMobile()? <TabularColumn key="mt_addr" data={(r) => <BasicText>{r.address}</BasicText>} title="Address" /> : null}
            {includeRelated? <TabularColumn key="div_mt" data={(r) => <BasicText>{Util.roundNice(r.metric_tonnes_diverted,2)}</BasicText>} title={window.PORTAL_COUNTRY == "USA" ? "Diverted (Tons)" : "Diverted (MT)"} /> : null}
            <TabularColumn key="div_percent" data={(r) => <BasicText>{Util.roundNice(r[boundRef.current.showing], 2)}</BasicText>} title={title} />
        </Tabular>
    </RankingTable>;
}


const SiteRanking = ({ rawData }) => {
    return <ShinyBlockOuter minHeight={400}>
        <ShinyHeading>
            <MetricStackTitle>{"Site Ranking"}</MetricStackTitle>
        </ShinyHeading>
        <ShinyBody>
            <SiteRankingTabular rawData={rawData}/>
        </ShinyBody>
    </ShinyBlockOuter>;
}

const SiteComparison = ({ portalData, startDate, endDate }) => {
    return <PageWrapper>
        <GroupComparison portalData={portalData} rawData={portalData.adjustedSiteData}/>
        <SiteRanking rawData={portalData.adjustedSiteData} />
    </PageWrapper>;
}

export { SiteRankingTabular };
export default SiteComparison;